import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import './LandingPage.css';

export default function LandingPage() {
  const { signInWithGoogle, continueAsGuest } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleGoogle = async () => {
    setLoading(true);
    setError('');
    try {
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (e: any) {
      setError(e.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGuest = () => {
    continueAsGuest();
    navigate('/dashboard');
  };

  return (
    <div className="landing">
      {/* Nav */}
      <nav className="landing-nav">
        <div className="landing-nav-brand">
          <span className="brand-icon">◆</span>
          <span className="landing-brand-name">Nexel</span>
        </div>
        <div className="landing-nav-actions">
          <button className="theme-toggle" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handleGuest}>Try free</button>
          <button className="btn btn-primary btn-sm" onClick={handleGoogle} disabled={loading}>Sign in</button>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="hero-badge">AI-powered Excel intelligence</div>
        <div className="beta-badge">Free during beta</div>
        <h1 className="hero-title">
          Your Excel data,<br />
          <span className="hero-gradient">finally understood.</span>
        </h1>
        <p className="hero-subtitle">
          Upload any Excel file. Detect every table at any position. Map formula dependencies.
          Query your data in plain English. Export clean results. All in one place.
        </p>

        <div className="hero-actions">
          <button className="btn btn-primary hero-btn" onClick={handleGoogle} disabled={loading}>
            {loading ? <span className="spinner" /> : (
              <>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>
          <button className="btn btn-secondary hero-btn" onClick={handleGuest}>
            Try as Guest — no signup
          </button>
        </div>
        {error && <p className="hero-error">{error}</p>}

        {/* Demo video — autoplays like a GIF */}
        <div className="hero-demo">
          <video
            className="hero-demo-video"
            src="/demo.mp4"
            autoPlay
            loop
            muted
            playsInline
          />
        </div>
      </section>

      {/* How it works */}
      <section className="landing-steps">
        <h2 className="section-title">How it works</h2>
        <div className="steps-grid">
          {STEPS.map((s, i) => (
            <div key={i} className="step-card">
              <div className="step-number">{i + 1}</div>
              <h3 className="step-title">{s.title}</h3>
              <p className="step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="landing-features">
        <h2 className="section-title">Built for messy, real-world Excel files</h2>
        <p className="section-subtitle">Multiple tables per sheet. Cross-sheet formulas. Dozens of sheets. Files that crash normal viewers. We handle all of it.</p>
        <div className="features-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="feature-card">
              <span className="feature-icon">{f.icon}</span>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-faq">
        <h2 className="section-title">Frequently asked questions</h2>
        <div className="faq-list">
          {FAQ.map((item, i) => (
            <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
              <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span>{item.q}</span>
                <span className="faq-toggle">{openFaq === i ? '−' : '+'}</span>
              </button>
              {openFaq === i && <p className="faq-answer">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta">
        <h2 className="cta-title">Ready to understand your Excel data?</h2>
        <p className="cta-subtitle">Free to start. No credit card required.</p>
        <div className="hero-actions">
          <button className="btn btn-primary hero-btn" onClick={handleGoogle} disabled={loading}>Get started free</button>
          <button className="btn btn-secondary hero-btn" onClick={handleGuest}>Try as Guest</button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-links">
          <span>© 2026 Nexel</span>
          <span className="footer-dot">·</span>
          <span>Built for analysts, by analysts</span>
        </div>
        <div className="footer-links">
          <a href="#privacy" className="footer-link">Privacy</a>
          <a href="#terms" className="footer-link">Terms</a>
          <a href="mailto:support@nexel.app" className="footer-link">Contact</a>
        </div>
      </footer>
    </div>
  );
}

const STEPS = [
  { title: 'Upload', desc: 'Drop any .xlsx or .xls file. We detect every table at any cell position across all sheets.' },
  { title: 'Explore', desc: 'See your data visualised with table maps, column types, formula dependencies, and sample previews.' },
  { title: 'Ask', desc: 'Chat with your data in plain English. Get an execution plan, confirm it, then run on your full dataset.' },
  { title: 'Export', desc: 'Download results as Excel, CSV, PDF, or Markdown. One click.' },
];

const FEATURES = [
  { icon: '🔍', title: 'Smart Table Detection', desc: 'Finds every table at any cell position — B10, D3, wherever. Multiple tables per sheet, merged cells, mixed types.' },
  { icon: '🔗', title: 'Formula Dependencies', desc: 'Maps cross-sheet formula references. See exactly which tables depend on which, with cell-level precision.' },
  { icon: '💬', title: 'AI-Powered Chat', desc: 'Ask questions in plain English. Review the execution plan before running. Auto-corrects up to 5 times.' },
  { icon: '⚡', title: 'Instant Operations', desc: 'Filter, sort, aggregate, pivot, merge, deduplicate — no AI needed. Deterministic and instant.' },
  { icon: '🏢', title: 'Team Collaboration', desc: 'Create organisations, invite members, share files, manage roles. Built for finance and ops teams.' },
  { icon: '🔒', title: 'Secure by Design', desc: 'Firebase auth, encrypted storage, code safety scanning, isolated sessions. Your data stays yours.' },
];

const FAQ = [
  { q: 'What file formats does Nexel support?', a: 'Nexel supports .xlsx, .xls, .xlsm, .xltx, and .xltm files up to 100MB. We handle multi-sheet workbooks with complex formulas and multiple tables per sheet.' },
  { q: 'How does the AI chat work?', a: 'You ask questions in plain English. Nexel generates an execution plan showing exactly what operations will run on your data. You review and approve the plan before it executes — no surprises.' },
  { q: 'Is my data secure?', a: 'Yes. Files are encrypted in transit and at rest. Each session is isolated. We use Firebase authentication and all AI-generated code is safety-scanned before execution.' },
  { q: 'Can I use Nexel without signing up?', a: 'Absolutely. Click "Try as Guest" to start immediately. Your data is stored in a temporary session. Sign in with Google anytime to save your work permanently.' },
];
